import React, { useState, useEffect, useRef, useCallback } from 'react';
import Home from './components/Home';
import Lobby from './components/Lobby';
import Game from './components/Game';
import ThatsYou from './components/ThatsYou';
import Mystery from './components/Mystery';
import StoryBuilder from './components/StoryBuilder';
import WordGuess from './components/WordGuess';
import Complete from './components/Complete';
import Trivia from './components/Trivia';
import CountryCity from './components/CountryCity';
import FakeArtist from './components/FakeArtist';
import { playMessage } from './sounds';

// Standalone FloatingChat component - NOT recreated on each message update
function FloatingChat({ messages, onSend, myPlayerId }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  const [shake, setShake] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevCount = useRef(messages.length);

  useEffect(() => {
    if (!open && messages.length > prevCount.current) {
      const newCount = messages.length - prevCount.current;
      setUnread((u) => u + newCount);
      setShake(true);
      setTimeout(() => setShake(false), 600);
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      playMessage();
    }
    prevCount.current = messages.length;
  }, [messages.length, open]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, messages.length]);

  const doSend = () => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
    inputRef.current?.focus();
  };

  return (
    <>
      <style>{`
        @keyframes chatShake {
          0%, 100% { transform: scale(1) rotate(0deg); }
          15% { transform: scale(1.2) rotate(-8deg); }
          30% { transform: scale(1.2) rotate(8deg); }
          45% { transform: scale(1.15) rotate(-5deg); }
          60% { transform: scale(1.15) rotate(5deg); }
          75% { transform: scale(1.1) rotate(-2deg); }
        }
        @keyframes chatPulse {
          0%, 100% { box-shadow: 0 4px 15px rgba(124,77,255,0.4); }
          50% { box-shadow: 0 4px 25px rgba(124,77,255,0.7), 0 0 40px rgba(124,77,255,0.3); }
        }
      `}</style>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', bottom: '20px', left: '20px', zIndex: 900,
            width: '60px', height: '60px', borderRadius: '50%',
            background: 'var(--terracotta)', border: 'none', color: 'white',
            fontSize: '1.6rem', cursor: 'pointer',
            boxShadow: 'var(--shadow-glow-coral)',
            animation: shake ? 'chatShake 0.6s ease' : (unread > 0 ? 'chatPulse 1.5s infinite' : 'none'),
            transition: 'transform 0.2s',
          }}
        >
          💬
          {unread > 0 && (
            <span style={{
              position: 'absolute', top: '-6px', right: '-6px',
              background: 'var(--ink)', borderRadius: '50%',
              width: '26px', height: '26px', fontSize: '0.8rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 900, border: '2px solid var(--surface)', color: 'var(--terracotta-soft)',
              boxShadow: 'var(--shadow-2)',
            }}>{unread > 9 ? '9+' : unread}</span>
          )}
        </button>
      )}
      {open && (
        <div style={{
          position: 'fixed', bottom: '12px', left: '12px', right: '12px', zIndex: 900,
          maxWidth: '420px', height: '55vh', maxHeight: '450px', minHeight: '280px',
          background: 'var(--surface-raised)', borderRadius: '20px',
          border: '1px solid var(--hairline)',
          boxShadow: 'var(--shadow-4)',
          display: 'flex', flexDirection: 'column',
          fontFamily: 'Heebo, sans-serif',
          animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 18px', borderBottom: '1px solid var(--hairline)',
          }}>
            <span style={{ fontWeight: 800, color: 'var(--ink)', fontSize: '1rem' }}>💬 צ'אט</span>
            <button onClick={() => setOpen(false)} style={{
              background: 'var(--surface-sunk)', border: 'none', color: 'var(--ink-2)',
              fontSize: '1.1rem', cursor: 'pointer', width: '32px', height: '32px',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>✕</button>
          </div>
          <div style={{
            flex: 1, overflowY: 'auto', padding: '10px 14px',
            display: 'flex', flexDirection: 'column', gap: '6px',
          }}>
            {messages.length === 0 && (
              <p style={{ color: 'var(--ink-3)', fontSize: '0.9rem', textAlign: 'center', marginTop: '30px' }}>
                אין הודעות עדיין - כתבו משהו!
              </p>
            )}
            {messages.map((msg, i) => {
              const isMe = msg.playerId === myPlayerId;
              return (
                <div key={i} style={{
                  direction: 'rtl', fontSize: '0.95rem',
                  background: isMe ? 'var(--terracotta-wash)' : 'var(--surface-sunk)',
                  color: 'var(--ink)',
                  padding: '8px 14px', borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  border: isMe ? '1px solid var(--terracotta-soft)' : '1px solid var(--hairline)',
                  maxWidth: '85%', alignSelf: isMe ? 'flex-end' : 'flex-start',
                }}>
                  {!isMe && <span style={{ fontWeight: 700, color: 'var(--terracotta-deep)', fontSize: '0.8rem', display: 'block', marginBottom: '2px' }}>{msg.playerName}</span>}
                  <span style={{ color: 'var(--ink)', lineHeight: 1.4 }}>{msg.text}</span>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>
          <div style={{
            display: 'flex', gap: '8px', padding: '12px 14px',
            borderTop: '1px solid var(--hairline)',
            background: 'var(--surface-sunk)',
            borderRadius: '0 0 20px 20px',
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSend()}
              placeholder="כתבו הודעה..."
              maxLength={200}
              style={{
                flex: 1, padding: '12px 16px', fontSize: '1rem', direction: 'rtl',
                borderRadius: '14px', border: '1.5px solid transparent',
                background: 'var(--surface-raised)', color: 'var(--ink)',
                fontFamily: 'Heebo, sans-serif', outline: 'none',
                boxShadow: 'inset 0 1px 2px rgba(60, 50, 45, 0.06)',
              }}
            />
            <button
              onClick={doSend}
              disabled={!input.trim()}
              style={{
                padding: '12px 20px', fontSize: '1rem', fontWeight: 700,
                borderRadius: '14px', border: 'none', cursor: 'pointer',
                background: input.trim() ? 'var(--terracotta)' : 'var(--surface-raised)',
                color: input.trim() ? 'white' : 'var(--ink-3)',
                fontFamily: 'Heebo, sans-serif', transition: 'all 0.2s',
                opacity: input.trim() ? 1 : 0.6,
                boxShadow: input.trim() ? 'var(--shadow-glow-coral)' : 'none',
              }}
            >
              שלח
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function FeedbackButton({ savedName }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setSent(false); setText(''); }}
        style={{
          position: 'fixed', bottom: '20px', right: '20px', zIndex: 800,
          width: '44px', height: '44px', borderRadius: '50%',
          background: 'var(--surface-raised)', border: '1px solid var(--hairline)',
          color: 'var(--ink-2)', fontSize: '1.1rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: 'var(--shadow-2)',
        }}
        title="הצעה לשיפור"
      >
        💡
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(43, 38, 34, 0.5)',
      backdropFilter: 'blur(10px) saturate(1.2)', WebkitBackdropFilter: 'blur(10px) saturate(1.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      padding: '20px',
    }} onClick={() => setOpen(false)}>
      <div style={{
        background: 'var(--surface-raised)', borderRadius: '20px', padding: '24px',
        maxWidth: '400px', width: '100%', border: '1px solid var(--hairline)',
        boxShadow: 'var(--shadow-4)',
      }} onClick={e => e.stopPropagation()}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🙏</div>
            <h3 style={{ color: 'var(--success)', marginBottom: '8px' }}>תודה רבה!</h3>
            <p style={{ color: 'var(--ink-2)', fontSize: '0.9rem' }}>ההצעה שלכם התקבלה</p>
            <button className="btn btn-ghost" onClick={() => setOpen(false)} style={{ marginTop: '16px' }}>
              סגור
            </button>
          </div>
        ) : (
          <>
            <h3 style={{ textAlign: 'center', color: 'var(--terracotta-deep)', marginBottom: '16px', fontSize: '1.1rem' }}>
              💡 הצעות לשיפור
            </h3>
            <p style={{ color: 'var(--ink-2)', fontSize: '0.9rem', textAlign: 'center', marginBottom: '16px' }}>
              יש לכם רעיון למשחק חדש? שיפור? באג? כתבו לנו!
            </p>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="כתבו כאן..."
              maxLength={500}
              autoFocus
              style={{
                width: '100%', minHeight: '120px', padding: '14px', borderRadius: '14px',
                background: 'var(--surface-sunk)', border: '1.5px solid transparent',
                color: 'var(--ink)', fontSize: '0.95rem', fontFamily: 'Heebo, sans-serif',
                resize: 'vertical', direction: 'rtl', outline: 'none', boxSizing: 'border-box',
                boxShadow: 'inset 0 1px 2px rgba(60, 50, 45, 0.06)',
              }}
            />
            <p style={{ textAlign: 'left', fontSize: '0.72rem', color: 'var(--ink-3)', marginTop: '4px' }}>
              {text.length}/500
            </p>
            <button
              className="btn btn-primary"
              disabled={text.trim().length < 2}
              onClick={() => {
                fetch('/api/feedback', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ text: text.trim(), name: savedName || '' }),
                }).then(r => r.json()).then(() => setSent(true)).catch(() => setSent(true));
              }}
              style={{ marginTop: '8px' }}
            >
              שלח הצעה ✨
            </button>
            <button className="btn btn-ghost" onClick={() => setOpen(false)}>
              ביטול
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function getRoomCodeFromURL() {
  const match = window.location.pathname.match(/\/game\/([A-Za-z0-9]+)/);
  return match ? match[1].toUpperCase() : null;
}

// Persistent storage helpers
function saveToStorage(key, value) {
  try { localStorage.setItem(key, value); } catch (e) {}
}
function getFromStorage(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}

function loadStats() {
  try {
    const s = localStorage.getItem('spyq_stats');
    return s ? JSON.parse(s) : { gamesPlayed: 0, gamesWon: 0, totalScore: 0 };
  } catch { return { gamesPlayed: 0, gamesWon: 0, totalScore: 0 }; }
}

function updateStats(gameState, playerId) {
  try {
    const stats = loadStats();
    stats.gamesPlayed++;
    const me = gameState.players?.find((p) => p.id === playerId);
    if (me) stats.totalScore += me.score || 0;
    const sorted = [...(gameState.players || [])].sort((a, b) => b.score - a.score);
    if (sorted[0]?.id === playerId) stats.gamesWon++;
    localStorage.setItem('spyq_stats', JSON.stringify(stats));
  } catch {}
}

function App() {
  const [screen, setScreen] = useState('home');
  const [ws, setWs] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [roomData, setRoomData] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [error, setError] = useState('');
  const [disconnected, setDisconnected] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const wsRef = useRef(null);
  const urlRoomCode = useRef(getRoomCodeFromURL());
  const reconnectAttempts = useRef(0);
  const playerIdRef = useRef(null);
  const gameStateRef = useRef(null);

  const savedName = getFromStorage('spyq_playerName') || '';

  const saveSession = useCallback((pid, code, name) => {
    saveToStorage('spyq_playerId', pid);
    saveToStorage('spyq_roomCode', code);
    if (name) saveToStorage('spyq_playerName', name);
  }, []);

  const getSavedSession = useCallback(() => {
    return {
      playerId: getFromStorage('spyq_playerId'),
      roomCode: getFromStorage('spyq_roomCode'),
    };
  }, []);

  const connect = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = window.location.port;
    const url = port ? `${protocol}//${host}:${port}` : `${protocol}//${host}`;

    const socket = new WebSocket(url);

    socket.onopen = () => {
      setWs(socket);
      wsRef.current = socket;
      setDisconnected(false);
      reconnectAttempts.current = 0;

      // Try to rejoin saved session
      const saved = getSavedSession();
      if (saved.playerId && saved.roomCode) {
        socket.send(JSON.stringify({
          type: 'REJOIN',
          payload: { oldPlayerId: saved.playerId, roomCode: saved.roomCode },
        }));
      }
    };

    socket.onmessage = (event) => {
      const { type, payload } = JSON.parse(event.data);

      switch (type) {
        case 'CONNECTED':
          setPlayerId(payload.playerId);
          playerIdRef.current = payload.playerId;
          break;

        case 'REJOINED':
          setPlayerId(payload.playerId);
          playerIdRef.current = payload.playerId;
          setRoomCode(payload.roomCode);
          setScreen('lobby');
          setError('');
          break;

        case 'ROOM_CREATED':
          setPlayerId(payload.playerId);
          playerIdRef.current = payload.playerId;
          setRoomCode(payload.roomCode);
          saveSession(payload.playerId, payload.roomCode);
          setScreen('lobby');
          setError('');
          break;

        case 'ROOM_JOINED':
          setPlayerId(payload.playerId);
          playerIdRef.current = payload.playerId;
          setRoomCode(payload.roomCode);
          saveSession(payload.playerId, payload.roomCode);
          setScreen('lobby');
          setError('');
          break;

        case 'ROOM_UPDATE':
          setRoomData(payload);
          if (payload.state === 'lobby') {
            setScreen('lobby');
            setGameState(null);
            gameStateRef.current = null;
          }
          break;

        case 'GAME_STATE':
          if (payload.gameOver && !gameStateRef.current?.gameOver) {
            updateStats(payload, playerIdRef.current);
          }
          setGameState(payload);
          gameStateRef.current = payload;
          setScreen('game');
          break;

        case 'CHAT_UPDATE':
          setChatMessages(payload.messages || []);
          break;

        case 'ERROR':
          setError(payload.message);
          break;

        default:
          break;
      }
    };

    socket.onerror = () => {
      // Suppress error noise - onclose will handle reconnect
    };

    socket.onclose = () => {
      setWs(null);
      wsRef.current = null;

      // Show disconnect only after 2 seconds of being disconnected
      const disconnectTimeout = setTimeout(() => setDisconnected(true), 2000);

      // Fast reconnect: 500ms, 1s, 2s, 4s (cap at 4s, not 8s)
      const delay = Math.min(500 * Math.pow(2, reconnectAttempts.current), 4000);
      reconnectAttempts.current++;
      setTimeout(() => {
        clearTimeout(disconnectTimeout);
        connect();
      }, delay);
    };

    return socket;
  }, [getSavedSession, saveSession]);

  useEffect(() => {
    const socket = connect();

    // Instant reconnect when returning to app (instead of waiting for backoff)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          reconnectAttempts.current = 0;
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Keepalive ping every 15s to prevent idle timeout
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'PING' }));
      } else if (wsRef.current && wsRef.current.readyState !== WebSocket.CONNECTING) {
        // Connection dead but not reconnecting - force reconnect
        reconnectAttempts.current = 0;
        connect();
      }
    }, 15000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(pingInterval);
      if (socket) socket.close();
    };
  }, [connect]);

  const sendMessage = useCallback((type, payload) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    }
  }, []);

  const clearError = useCallback(() => setError(''), []);

  const DisconnectBanner = disconnected ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 999,
      background: 'var(--ink)',
      color: 'var(--surface)', textAlign: 'center',
      padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600,
      fontFamily: 'Heebo, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      boxShadow: 'var(--shadow-3)',
    }}>
      <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
      מתחבר מחדש... רגע
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  ) : null;

  if (screen === 'game' && gameState) {
    const modeMap = { thatsyou: ThatsYou, mystery: Mystery, story: StoryBuilder, wordguess: WordGuess, complete: Complete, trivia: Trivia, countrycity: CountryCity, fakeartist: FakeArtist };
    const GameComponent = modeMap[gameState.mode] || Game;
    return (
      <div className="app">
        {DisconnectBanner}
        <GameComponent
          gameState={gameState}
          playerId={playerId}
          roomCode={roomCode}
          sendMessage={sendMessage}
          error={error}
          clearError={clearError}
        />
        {gameState.chatEnabled && (
          <FloatingChat
            messages={chatMessages}
            onSend={(text) => sendMessage('GAME_CHAT', { text })}
            myPlayerId={playerIdRef.current}
          />
        )}
        <FeedbackButton savedName={savedName} />
      </div>
    );
  }

  if (screen === 'lobby' && roomData) {
    return (
      <div className="app">
        {DisconnectBanner}
        <Lobby
          roomData={roomData}
          roomCode={roomCode}
          playerId={playerId}
          sendMessage={sendMessage}
          error={error}
          clearError={clearError}
        />
        <FeedbackButton savedName={savedName} />
      </div>
    );
  }

  return (
    <div className="app">
      {DisconnectBanner}
      <Home
        ws={ws}
        sendMessage={sendMessage}
        error={error}
        clearError={clearError}
        initialRoomCode={urlRoomCode.current}
        savedName={savedName}
        onSaveName={(name) => saveToStorage('spyq_playerName', name)}
      />
    </div>
  );
}

export default App;
